import BigNumber from "bignumber.js";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  createCrossMerchantTransferRequest,
  getBalance,
  getLiquidityBalance,
} from "../../../../api/merchant";
import DialogInOne from "../../../../components/DialogInOne";
import { useAssets } from "../../../../components/FetchConfig";
import MpTextField from "../../../../components/MpTextField";
import MpTextFieldNumberOnly from "../../../../components/MpTextFieldNumberOnly";
import { Box, SelectChangeEvent } from "../../../../components/MuiGenerals";
import YubiPrompter from "../../../../components/Prompter";
import { useAlerting, useTranslation, useYubiPrompt } from "../../../../hooks";
import { selectCrossMerchant } from "../../../../reducer/crossMerchantSlice";
import { useAppSelector } from "../../../../reducer/hooks";
import {
  displayAmount,
  findChainInfo,
  getDecimal,
} from "../../../../utils/helper";
import { useZusDialogStore } from "../../../../zustand/store";
import { translateKeyObj as TK, translatePrefix } from "../config";
import { setCreateRequest, useReduxContext } from "../reduxContext";
import useLiquidityBalanceQuery from "../useLiquidityBalanceQuery";

interface Ifields {
  client_id_from: string;
  client_id_to: string;
  transaction_amount_from: string;
  balanceFrom?: string;
  balanceTo?: string;
}

const initFields: Ifields = {
  client_id_from: "",
  client_id_to: "",
  transaction_amount_from: "",
  balanceFrom: "",
  balanceTo: "",
};

const CreateRequestDialog = () => {
  const { t } = useTranslation(translatePrefix);
  const { alerting } = useAlerting();
  const zusDialog = useZusDialogStore();

  const [fields, setFields] = useState(initFields);
  const [state, dispatch] = useReduxContext();

  const { selectAssetsByChain } = useAssets();

  const {
    from_chain_id = "",
    to_chain_id = "",
    from_merchant_id = "",
    from_chain_type = "",
    to_merchant_id = "",
    to_chain_type = "",
    from_asset_name,
    to_asset_name,
    liquidity_provider,
    cross_merchant_transfer_exchange_rate,
    liquidity_provider_merchant_id,
  } = state.createRequest ?? {};

  const { merchantMapping } = useAppSelector(selectCrossMerchant);

  const { getResAfterYubi, prompterConfig } = useYubiPrompt();

  const safeMultiply = (num1: number | string, num2: number | string) => {
    return BigNumber(num1).multipliedBy(num2).toFixed().toString();
  };

  const onChange =
    (type: keyof Ifields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const { data: liquidityBalanceRes } = useLiquidityBalanceQuery();

  const { name: to_chain_name } =
    findChainInfo(to_chain_type, to_chain_id) || {};

  const { name: from_chain_name } =
    findChainInfo(from_chain_type, from_chain_id) || {};

  const getMerchantName = (merchantId: string) => merchantMapping[merchantId];

  const exchangeRate = cross_merchant_transfer_exchange_rate?.exchange_rate;

  const calBalance = (
    balance?: string,
    frozenBalance?: string,
    credit?: string
  ) => {
    return BigNumber(balance ?? 0)
      .minus(frozenBalance ?? 0)
      .plus(credit ?? 0)
      .toFixed()
      .toString();
  };

  interface IGetCryptoDecimal {
    chainType: string | number;
    chainId: string | number;
    assetName: string;
  }

  const getCryptoDecimal = (props: IGetCryptoDecimal) => {
    const { chainType, chainId, assetName } = props;

    const assets = selectAssetsByChain({
      chain_type: chainType,
      chain_id: chainId,
    });

    const decimals =
      assets.find((item) => item.asset_name === assetName)?.decimals || 0;

    return decimals;
  };

  const onClientIdTextFieldBlur =
    (fromTo: "from" | "to") =>
    async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (!value && fromTo === "from") {
        return setFields((fields) => ({ ...fields, balanceFrom: "" }));
      }

      const chainType = from_chain_type;
      const chainId = from_chain_id;
      const assetName = from_asset_name;
      const fieldName = "balanceFrom";

      zusDialog.openExtra("loadingDialog");
      const res = await getBalance({
        assetName: assetName || "",
        clientId: String(value),
        chainType: Number(chainType),
        chainId: chainId,
      });
      zusDialog.closeExtra();

      if (!res) {
        return setFields((fields) => ({ ...fields, [fieldName]: "" }));
      }

      const fromBalance = calBalance(
        res?.balance,
        res?.frozen_balance,
        res?.credit
      );

      const decimals = getCryptoDecimal({
        chainType,
        chainId,
        assetName: assetName || "",
      });

      const balanceValue = displayAmount(fromBalance, decimals) || "0";

      setFields((fields) => ({ ...fields, [fieldName]: balanceValue }));
    };

  const txAmountTo = safeMultiply(
    fields.transaction_amount_from || 0,
    exchangeRate || 0
  );

  const liquidityBalance = calBalance(
    liquidityBalanceRes?.balance,
    liquidityBalanceRes?.frozen_balance,
    liquidityBalanceRes?.credit
  );
  const liquidtyBalnceDeciamls = getCryptoDecimal({
    chainType: to_chain_type,
    chainId: to_chain_id,
    assetName: to_asset_name || "",
  });
  const liquidityBalanceDisplay = displayAmount(
    liquidityBalance,
    liquidtyBalnceDeciamls
  );

  const genBoxData: Array<[string, JSX.Element]> = [
    [t(TK.merchantFrom), <>{getMerchantName(from_merchant_id)}</>],
    [t(TK.merchantTo), <>{getMerchantName(to_merchant_id)}</>],
    [t(TK.chainNameFrom), <>{from_chain_name}</>],
    [t(TK.chainNameTo), <>{to_chain_name}</>],
    [t(TK.currencyFrom), <>{from_asset_name}</>],
    [t(TK.currencyTo), <>{to_asset_name}</>],
    [
      t(TK.clientIdFrom),
      <MpTextField
        label={t(TK.phClientIdFrom)}
        value={fields.client_id_from}
        onChange={onChange("client_id_from")}
        onBlur={onClientIdTextFieldBlur("from")}
      />,
    ],
    [
      t(TK.clientIdTo),
      <MpTextField
        label={t(TK.phClientIdTo)}
        value={fields.client_id_to}
        onChange={onChange("client_id_to")}
      />,
    ],
    [t(TK.balanceFrom), <div>{fields.balanceFrom}</div>],
    ["", <></>],
    [
      t(TK.merchantLp),
      <div>{getMerchantName(liquidity_provider_merchant_id || "")}</div>,
    ],
    [t(TK.liquidityProvider), <>{liquidity_provider}</>],
    [t(TK.exchangeRate), <div>{exchangeRate}</div>],
    [
      t(TK.liquidityBalance, { asset: to_asset_name }),
      <>{liquidityBalanceDisplay}</>,
    ],
    [
      t(TK.transactionAmountFrom),
      <MpTextFieldNumberOnly
        label={t(TK.phTransactionAmountFrom)}
        value={fields.transaction_amount_from}
        onChange={onChange("transaction_amount_from")}
      />,
    ],
    [t(TK.transactionAmountTo), <div>{txAmountTo}</div>],
  ];

  const amountToCryptoValue = (value: string, decimal: number) => {
    return safeMultiply(value, Math.pow(10, decimal));
  };

  const handleOnConfirm = async () => {
    const mandatoryFieldsEntries = Object.entries(fields).filter(
      ([key]) => key !== "balanceFrom" && key !== "balanceTo"
    );

    const emptyFieldsEntries = mandatoryFieldsEntries.filter(
      ([_key, value]) => !value
    );

    const firstEmptyFieldEntries: [key: string, value: string] =
      emptyFieldsEntries[0];

    if (firstEmptyFieldEntries) {
      const fieldForDisplay = t(firstEmptyFieldEntries[0]);
      alerting(
        "error",
        t(TK.createWarning, { verb: t(TK.enter), field: fieldForDisplay })
      );
      return;
    }

    const amountDecimals =
      selectAssetsByChain({
        chain_type: from_chain_type,
        chain_id: from_chain_id,
      }).find((item) => item.asset_name === from_asset_name)?.decimals || 0;

    const res = await getResAfterYubi(createCrossMerchantTransferRequest, {
      from_merchant_id: from_merchant_id,
      from_chain_type: from_chain_type,
      from_chain_id: from_chain_id,
      from_asset_name: from_asset_name,
      to_merchant_id: to_merchant_id,
      to_chain_type: to_chain_type,
      to_chain_id: to_chain_id,
      to_asset_name: to_asset_name,
      liquidity_provider: liquidity_provider,
      amount: amountToCryptoValue(
        fields.transaction_amount_from,
        amountDecimals
      ),
      decimals: amountDecimals,
      nonce: new Date().getTime(),
      from_client_id: fields.client_id_from,
      to_client_id: fields.client_id_to,
      exchange_rate: exchangeRate,
    });

    if (!res) return;

    alerting("success", t(TK.createRequestSuccess));
    handleOnClose();
  };

  const handleOnClose = () => {
    dispatch(setCreateRequest(undefined));
    setFields(initFields);
  };

  const dialogConfig = {
    title: t(TK.createRequest),
    self: {
      open: state.createRequest !== undefined,
      onClose: handleOnClose,
    },
    content: <GridBox data={genBoxData} />,
    onCancel: handleOnClose,
    onConfirm: handleOnConfirm,
  } as const;

  return (
    <>
      <YubiPrompter {...prompterConfig} />
      <DialogInOne
        {...dialogConfig}
        rest={{
          maxWidth: "md",
          closeAfterTransition: false,
        }}
      />
    </>
  );
};

export default CreateRequestDialog;

const GridBox = (props: { data: Array<[string, JSX.Element]> }) => {
  const { data } = props;
  return (
    <Box
      sx={{
        ".MuiInputLabel-shrink": {
          display: "none",
        },
        display: "grid",
        gridTemplateColumns: "minmax(0,5fr) minmax(0,5fr)",
        columnGap: "20px",
        fontSize: "0.6875rem",
      }}
    >
      {data.map(([label, CustomElement]) => (
        <Box
          key={label}
          sx={{
            display: "flex",
            margin: "12px 0px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Box sx={{ marginRight: "8px", flex: "1" }}>
            {label && `${label}:`}
          </Box>
          <Box
            sx={{
              flex: "3",
              minWidth: "0",
              ">div": { width: "100%", margin: 0 },
            }}
          >
            {CustomElement}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
